import { createCSSManager } from "@repo/mfe-core/host";
import { getMfeByName, loadRemote } from "@repo/mfe-registry";
import type { MfeModule } from "@repo/mfe-registry";
import { Box, Loader } from "@saviynt/design-system";
import { Suspense, useCallback, useEffect, useState } from "react";

import { MfeErrorBoundary } from "./MfeErrorBoundary";
import { MfeErrorState } from "./MfeErrorState";

interface MfeLoaderProps {
  name: string;
  fallback?: React.ReactNode;
  props?: Record<string, unknown>;
  hostManagesLoading?: boolean;
}

/**
 * Module-level singleton CSS Manager instance.
 * Shared across all MfeLoader instances so inject/remove are coordinated.
 */
const cssManager = createCSSManager({
  onError: (mfeName, error) => {
    console.warn(`[MfeLoader] CSS issue for "${mfeName}":`, error.message);
  },
});

/**
 * Default CSS asset filename relative to the MFE's entry URL base path.
 * Guaranteed by createMfViteConfig: cssCodeSplit: false + assetFileNames: "assets/[name][extname]"
 */
const MFE_CSS_ASSET_PATH = "assets/style.css";

/**
 * Derives the CSS URL from the MFE's entry URL.
 * Strips the filename (e.g. remoteEntry.js) and appends the default CSS asset path.
 *
 * Examples:
 *   "/mfe/integrations/module-federation/remoteEntry.js"
 *     → "/mfe/integrations/module-federation/assets/style.css"
 *
 *   "http://localhost:3002/remoteEntry.js"
 *     → "http://localhost:3002/assets/style.css"
 */
function deriveCssUrl(entryUrl: string): string {
  const lastSlashIndex = entryUrl.lastIndexOf("/");
  const basePath =
    lastSlashIndex >= 0 ? entryUrl.substring(0, lastSlashIndex + 1) : "";
  return `${basePath}${MFE_CSS_ASSET_PATH}`;
}

const DefaultFallback = () => (
  <Box className="w-full h-full min-h-full flex justify-center items-center bg-[var(--color-page-subtle,#f2f4f8)]">
    <Loader kind="loop" format="main" color="brand" />
  </Box>
);

// Track pending CSS removal timers per MFE name.
// When the same MFE remounts (intra-MFE navigation), the new mount cancels the pending removal.
const pendingRemovals = new Map<string, ReturnType<typeof setTimeout>>();

export function MfeLoader({
  name,
  fallback = <DefaultFallback />,
  props = {},
  hostManagesLoading = false,
}: MfeLoaderProps) {
  const [Component, setComponent] = useState<React.ComponentType | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [key, setKey] = useState(0);

  const attemptLoad = useCallback(() => {
    setComponent(null);
    setError(null);
    setKey((k) => k + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;

    // Cancel any pending CSS removal for this MFE (prevents FOUC during intra-MFE navigation)
    const pendingTimer = pendingRemovals.get(name);
    if (pendingTimer) {
      clearTimeout(pendingTimer);
      pendingRemovals.delete(name);
    }

    setComponent(null);
    setError(null);

    // 1. Look up MFE in registry
    const mfe = getMfeByName(name);
    if (!mfe) {
      setError(new Error(`MFE "${name}" not found in registry`));
      return;
    }

    // 2. Derive CSS URL from the entry URL (or use explicit cssAssetPath if defined)
    const entryUrl =
      import.meta.env.PROD && mfe.moduleFederation.prodUrl
        ? mfe.moduleFederation.prodUrl
        : mfe.moduleFederation.devUrl;
    const cssUrl = mfe.moduleFederation.cssAssetPath || deriveCssUrl(entryUrl);

    // 3. Inject CSS, then load remote module
    async function loadMfe() {
      try {
        // Inject CSS and await (resolves on load, error, or timeout — graceful degradation)
        await cssManager.inject(name, cssUrl);

        if (cancelled) return;

        // Load the remote module via registry's loadRemote (backed by RuntimeLoader)
        const module: MfeModule = await loadRemote(name);

        if (cancelled) return;

        // Extract the component from the module
        const Comp = module.default || module.App;
        if (Comp) {
          setComponent(() => Comp as React.ComponentType);
        } else {
          setError(new Error("No component exported from remote"));
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err : new Error("Failed to load MFE"),
          );
        }
      }
    }

    loadMfe();

    // 4. Cleanup: delay CSS removal to avoid FOUC during intra-MFE route transitions.
    // When navigating between sub-routes of the same MFE (e.g., /integrations/ → /integrations/edit),
    // the old MfeLoader unmounts and a new one mounts in the same render cycle. Without delay,
    // the CSS <link> is removed and re-injected, causing a flash of unstyled content.
    return () => {
      cancelled = true;
      const timer = setTimeout(() => {
        cssManager.remove(name);
      }, 100);
      // If the same MFE remounts before the timer fires, React will run
      // the new effect's inject() which is idempotent — the link stays.
      // Store cleanup in a module-level map so the next mount can cancel it.
      pendingRemovals.set(name, timer);
    };
  }, [name, key]);

  // Load error — show full error state with retry + home (replaces the old inline text)
  if (error) {
    return (
      <MfeErrorState
        mfeName={name}
        error={error}
        onRetry={attemptLoad}
        onGoHome={() => {
          window.location.href = "/";
        }}
        isLoadError
      />
    );
  }

  if (!Component) {
    if (hostManagesLoading) {
      return null;
    }
    return <>{fallback}</>;
  }

  // Runtime error boundary — catches crashes inside the rendered MFE component
  return (
    <MfeErrorBoundary name={name} onRetry={() => setKey((k) => k + 1)}>
      <Suspense fallback={null}>
        <Component key={key} {...props} />
      </Suspense>
    </MfeErrorBoundary>
  );
}

export function useDynamicRemote(name: string) {
  const [module, setModule] = useState<MfeModule | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    loadRemote(name)
      .then((mod) => {
        setModule(mod);
        setLoading(false);
      })
      .catch((err) => {
        setError(err);
        setLoading(false);
      });
  }, [name]);

  return { module, loading, error };
}
